/**
 * Exceptions typées du domaine SYGAP.
 *
 * Invariant CLAUDE.md : « Toute erreur est journalisée avec son contexte et
 * remonte une exception typée du domaine — jamais un `Error` nu. » Le `contexte`
 * est destiné aux journaux ; il ne doit donc JAMAIS contenir de secret, de mot
 * de passe, de jeton ni de clé de signature.
 */
/** Codes stables, exploitables par l'API pour mapper une réponse HTTP. */
export const CODE_ERREUR = {
    /** Numéro officiel mal formé, type inconnu ou caractère de contrôle faux. */
    NUMERO_INVALIDE: 'SYGAP_NUMERO_INVALIDE',
    /** Paramètres de génération d'un numéro hors bornes (année, séquence…). */
    NUMERO_PARAMETRE_INVALIDE: 'SYGAP_NUMERO_PARAMETRE_INVALIDE',
    /** Payload impossible à canoniser : cycle, nombre non fini, valeur non JSON. */
    PAYLOAD_NON_CANONISABLE: 'SYGAP_PAYLOAD_NON_CANONISABLE',
    /** Clé de signature absente de l'environnement ou trop courte. */
    CLE_SIGNATURE_INDISPONIBLE: 'SYGAP_CLE_SIGNATURE_INDISPONIBLE',
    /** Référentiel ADMINISTRATION incohérent : ordre dupliqué, circuit vide. */
    CIRCUIT_INVALIDE: 'SYGAP_CIRCUIT_INVALIDE',
    /** Critère de tarification incohérent avec la catégorie d'embarcation. */
    TARIF_PARAMETRE_INVALIDE: 'SYGAP_TARIF_PARAMETRE_INVALIDE',
    /** Données de titre inaptes à la signature : champ vide, date mal formée, durée nulle. */
    DELIVRANCE_PARAMETRE_INVALIDE: 'SYGAP_DELIVRANCE_PARAMETRE_INVALIDE',
    /** Chiffrement ou déchiffrement d'un document impossible : clé, format, intégrité. */
    DOCUMENT_CHIFFREMENT: 'SYGAP_DOCUMENT_CHIFFREMENT',
    /** Donnée inencodable en code-barres : caractère hors jeu, chaîne vide. */
    CODE_BARRES_INVALIDE: 'SYGAP_CODE_BARRES_INVALIDE',
    /** Mot de passe refusé par la politique du cadrage §5.3. */
    MOT_DE_PASSE_FAIBLE: 'SYGAP_MOT_DE_PASSE_FAIBLE',
    /** Gabarit de message réclamant une variable que l'appelant n'a pas fournie. */
    NOTIFICATION_VARIABLE_MANQUANTE: 'SYGAP_NOTIFICATION_VARIABLE_MANQUANTE',
    /** Numéro de téléphone hors du plan de numérotation mobile guinéen. */
    NOTIFICATION_NUMERO_INVALIDE: 'SYGAP_NOTIFICATION_NUMERO_INVALIDE',
    /** Référentiel des canaux ou des modèles incohérent : canal inconnu, doublon. */
    NOTIFICATION_REFERENTIEL_INVALIDE: 'SYGAP_NOTIFICATION_REFERENTIEL_INVALIDE',
    /** Montant inexploitable : non fini, négatif, ou plus précis que la colonne. */
    PAIEMENT_MONTANT_INVALIDE: 'SYGAP_PAIEMENT_MONTANT_INVALIDE',
    /** Déclaration de captures invalide : espèce vide, en double, poids aberrant. */
    JOURNAL_CAPTURES_INVALIDES: 'SYGAP_JOURNAL_CAPTURES_INVALIDES',
    /** Ticket d'incident inexploitable : délai de catégorie absurde, numéro hors bornes. */
    TICKET_PARAMETRE_INVALIDE: 'SYGAP_TICKET_PARAMETRE_INVALIDE',
};
/** Racine de toutes les erreurs métier levées par `@sygap/core`. */
export class ErreurDomaineSygap extends Error {
    code;
    contexte;
    constructor(code, message, contexte = {}) {
        super(message);
        this.name = new.target.name;
        this.code = code;
        this.contexte = Object.freeze({ ...contexte });
    }
    /**
     * Détail que l'API a le droit de **renvoyer à l'appelant**, à la différence du
     * `contexte`, qui ne va qu'au journal.
     *
     * Vide par défaut : une erreur ne livre rien tant que quelqu'un n'a pas jugé,
     * cas par cas, que le détail aide l'usager sans renseigner un attaquant. Le
     * cas type est la politique de mot de passe — refuser sans dire ce qui manque
     * ferait recommencer à l'aveugle.
     */
    get detailPublic() {
        return {};
    }
}
/** Numérotation officielle : génération ou vérification impossible. */
export class ErreurNumerotation extends ErreurDomaineSygap {
    constructor(code, message, contexte = {}) {
        super(code, message, contexte);
    }
}
/** Signature, canonisation ou empreinte documentaire impossible. */
export class ErreurCryptoDocumentaire extends ErreurDomaineSygap {
    constructor(message, contexte = {}) {
        super(CODE_ERREUR.PAYLOAD_NON_CANONISABLE, message, contexte);
    }
}
/**
 * Clé de signature indisponible.
 * Le contexte ne porte que le NOM de la variable d'environnement, jamais sa valeur.
 */
export class ErreurConfigurationSecret extends ErreurDomaineSygap {
    constructor(message, nomVariable) {
        super(CODE_ERREUR.CLE_SIGNATURE_INDISPONIBLE, message, { nomVariable });
    }
}
/** Circuit d'instruction incohérent. */
export class ErreurCircuitInstruction extends ErreurDomaineSygap {
    constructor(message, contexte = {}) {
        super(CODE_ERREUR.CIRCUIT_INVALIDE, message, contexte);
    }
}
/**
 * Données de titre inaptes à la délivrance. Levée **avant** toute écriture : un
 * titre mal composé produirait une signature invérifiable sur le terrain.
 */
export class ErreurDelivrance extends ErreurDomaineSygap {
    constructor(code, message, contexte = {}) {
        super(code, message, contexte);
    }
}
/** Paramètres de sélection de tarif incohérents. */
export class ErreurTarification extends ErreurDomaineSygap {
    constructor(message, contexte = {}) {
        super(CODE_ERREUR.TARIF_PARAMETRE_INVALIDE, message, contexte);
    }
}
/**
 * Chiffrement ou déchiffrement d'un document impossible.
 *
 * Le contexte ne porte jamais ni la clé, ni le clair, ni le chiffré : un
 * document officiel qui fuit par un journal d'erreur est une fuite quand même.
 */
export class ErreurDocumentChiffre extends ErreurDomaineSygap {
    constructor(message, contexte = {}) {
        super(CODE_ERREUR.DOCUMENT_CHIFFREMENT, message, contexte);
    }
}
/**
 * Composition ou acheminement d'une notification impossible.
 *
 * Le contexte ne porte **jamais le corps rendu ni le numéro du destinataire** :
 * un message d'expiration nomme un titre, un titulaire et une échéance, et la
 * loi L/2016/037/AN ne permet pas de recopier cela dans un journal technique.
 * Seuls les noms des variables manquantes y figurent.
 */
export class ErreurNotification extends ErreurDomaineSygap {
    constructor(code, message, contexte = {}) {
        super(code, message, contexte);
    }
}
/**
 * Montant inexploitable par la comptabilité publique.
 *
 * Levée **avant** toute écriture : une recette enregistrée à un centième près
 * d'un flottant ne se rapproche plus d'un relevé d'opérateur.
 */
export class ErreurPaiement extends ErreurDomaineSygap {
    constructor(message, contexte = {}) {
        super(CODE_ERREUR.PAIEMENT_MONTANT_INVALIDE, message, contexte);
    }
}
/**
 * Déclaration de captures inexploitable.
 *
 * Levée **avant** toute écriture : un journal de pêche alimente la statistique
 * de production nationale, qui sert à fixer les quotas. Une ligne fausse ne
 * fait pas qu'afficher un mauvais chiffre, elle se propage dans une décision
 * d'aménagement.
 */
export class ErreurJournalPeche extends ErreurDomaineSygap {
    constructor(message, contexte = {}) {
        super(CODE_ERREUR.JOURNAL_CAPTURES_INVALIDES, message, contexte);
    }
}
/**
 * Paramètres de cycle de vie d'un ticket inexploitables.
 *
 * Levée **avant** toute écriture. Le contexte ne porte que des grandeurs —
 * délai, année, séquence, nom de champ : l'objet et la description d'un ticket
 * peuvent citer un dossier, un titulaire ou une pièce d'identité, et la loi
 * L/2016/037/AN ne permet pas de les recopier dans un journal technique.
 */
export class ErreurTicket extends ErreurDomaineSygap {
    constructor(message, contexte = {}) {
        super(CODE_ERREUR.TICKET_PARAMETRE_INVALIDE, message, contexte);
    }
}
/** Donnée inencodable en code-barres. */
export class ErreurCodeBarres extends ErreurDomaineSygap {
    constructor(message, contexte = {}) {
        super(CODE_ERREUR.CODE_BARRES_INVALIDE, message, contexte);
    }
}
//# sourceMappingURL=erreurs.js.map